/**
 * Infinite scroll for collection product grids.
 * Watches a sentinel element at the bottom of the grid and, when it
 * scrolls into view, fetches the next collection page and appends its
 * products to the current grid — no full page reload, no "page 2/3/4" clicks.
 *
 * Falls back gracefully: if JS fails to load or errors out, the
 * <noscript> pagination links rendered by the theme still work normally.
 */
(function () {
  'use strict';

  function InfiniteScrollGrid(root) {
    this.root = root;
    this.grid = root.querySelector('[data-infinite-grid]');
    this.footer = root.querySelector('[data-infinite-footer]');

    if (!this.grid || !this.footer) return;

    this.currentPage = parseInt(this.footer.getAttribute('data-current-page'), 10) || 1;
    this.totalPages = parseInt(this.footer.getAttribute('data-total-pages'), 10) || 1;
    this.loading = false;

    if (this.currentPage >= this.totalPages) {
      this.footer.setAttribute('hidden', 'hidden');
      return;
    }

    this.observer = new IntersectionObserver(this.handleIntersect.bind(this), {
      rootMargin: '800px 0px 800px 0px'
    });
    this.observer.observe(this.footer);
  }

  InfiniteScrollGrid.prototype.handleIntersect = function (entries) {
    var self = this;
    entries.forEach(function (entry) {
      if (entry.isIntersecting && !self.loading && self.currentPage < self.totalPages) {
        self.loadNextPage();
      }
    });
  };

  InfiniteScrollGrid.prototype.loadNextPage = function () {
    var self = this;
    this.loading = true;
    this.footer.classList.add('is-loading');

    var nextPage = this.currentPage + 1;
    var url = new URL(window.location.href);
    url.searchParams.set('page', nextPage);

    fetch(url.toString(), { credentials: 'same-origin' })
      .then(function (response) {
        if (!response.ok) throw new Error('Request failed: ' + response.status);
        return response.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var newGrid = doc.querySelector('[data-infinite-grid]');
        var newFooter = doc.querySelector('[data-infinite-footer]');

        if (newGrid) {
          var fragment = document.createDocumentFragment();
          Array.prototype.forEach.call(newGrid.children, function (child) {
            fragment.appendChild(child);
          });
          self.grid.appendChild(fragment);
        }

        self.currentPage = nextPage;
        self.footer.setAttribute('data-current-page', String(self.currentPage));

        if (newFooter) {
          var newTotal = parseInt(newFooter.getAttribute('data-total-pages'), 10);
          if (!isNaN(newTotal)) {
            self.totalPages = newTotal;
            self.footer.setAttribute('data-total-pages', String(newTotal));
          }
        }

        // Update the browser URL/history so refresh, back-button and
        // analytics still reflect how far the shopper has scrolled.
        if (window.history && window.history.replaceState) {
          window.history.replaceState({}, '', url.toString());
        }

        // Let the rest of the theme know new products landed in the DOM
        // (lazysizes picks up new .lazyload images on its own via its
        // built-in MutationObserver; this event is for anything else
        // that wants to hook in, e.g. review star widgets).
        window.dispatchEvent(new CustomEvent('infinite-scroll:loaded', {
          detail: { grid: self.grid, page: self.currentPage }
        }));

        self.loading = false;
        self.footer.classList.remove('is-loading');

        if (self.currentPage >= self.totalPages) {
          self.observer.unobserve(self.footer);
          self.footer.setAttribute('hidden', 'hidden');
        }
      })
      .catch(function (err) {
        console.error('[infinite-scroll]', err);
        self.loading = false;
        self.footer.classList.remove('is-loading');
        // Stop trying on repeated failure so we don't hammer the server.
        self.observer.unobserve(self.footer);
      });
  };

  function init() {
    document.querySelectorAll('[data-infinite-scroll]').forEach(function (root) {
      if (!root.infiniteScrollInstance) {
        root.infiniteScrollInstance = new InfiniteScrollGrid(root);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-init if the section is re-rendered by the theme editor.
  document.addEventListener('shopify:section:load', init);
})();
